import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import Select, { components as selectComponents } from 'react-select';
import { getAccessibleReports, getReportDefinition, runReport } from '../api/reportingAPI';
import { useTableFilters } from '../utils/useTableFilters';
import ReportDataTable from './ReportDataTable';
import { formatLocal } from '../shared/dates';
import { getUserTimeZone } from '../shared/timezone';

const PAGINATED_TO_DROPDOWN_MAP = {
  'assets': { api: 'assets/for-dropdown', displayField: (a) => `${a.unitNumber || a.assetNumber || ''} ${a.assetDescription || ''}` },
  'task-types': { api: 'task-types', displayField: 'taskTypeName' },
  'task-codes': { api: 'task-codes', displayField: 'name' },
  'modifier-codes': { api: 'modifier-codes', displayField: 'name' },
  'suppliers': { api: 'suppliers/for-dropdown', displayField: 'name' },
  'purchase-order-statuses': { api: 'purchase-order-status', displayField: 'status' },
  'cost-responsibility': { api: 'cost-responsibility', displayField: 'name' },
  'priorities': { api: 'priority', displayField: 'name' },
  'work-order-statuses': { api: 'work-order-status', displayField: 'name' },
  'teams': { api: 'teams', displayField: 'teamName' },
  'event-type': { api: 'event-type', displayField: 'name' },
  'event-status': { api: 'event-status', displayField: 'name' },
  'event-responsibility': { api: 'event-responsibility', displayField: 'name' },
  'event-location': { api: 'event-location', displayField: 'name' },
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ReportsLayer = () => {
  // State for report selection
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [reportDefinition, setReportDefinition] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingDefinition, setLoadingDefinition] = useState(false);
  const [activePanelTab, setActivePanelTab] = useState('columns');
  const [columnSearch, setColumnSearch] = useState('');

  // State for report execution
  const [reportData, setReportData] = useState(null);
  const [reportMetadata, setReportMetadata] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [error, setError] = useState(null);
  const runInFlightRef = useRef(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // Section by state
  const [sectionBy, setSectionBy] = useState('none');

  // Sort by state - array of { fieldName, ascending }
  const [sortByFields, setSortByFields] = useState([]);

  // Selected display fields state
  const [selectedFields, setSelectedFields] = useState([]);

  // Build table filters from report definition, converting paginatedDropdown to dropDown
  const tableFilters = useMemo(() => {
    if (!reportDefinition?.filterDefinition?.filters) return [];

    return reportDefinition.filterDefinition.filters.map((filter) => {
      const config = {
        field: filter.field,
        type: filter.type,
        label: filter.label,
        menuBarFilter: 1, // All filters inline
        isMulti: filter.isMulti ?? false,
        required: filter.required,
      };

      // Text filter placeholder
      if (filter.type === 'text') {
        config.placeholder = `Filter by ${filter.label.toLowerCase()}...`;
      }

      // Static options
      if (filter.options) {
        config.options = filter.options;
      }

      // API-based dropdown
      if (filter.api && filter.type === 'dropDown') {
        config.api = filter.api;
        config.displayField = filter.displayField;
      }

      // Convert paginatedDropdown to dropDown using the mapping
      if (filter.type === 'paginatedDropdown' && filter.api) {
        const mapping = PAGINATED_TO_DROPDOWN_MAP[filter.api];
        if (mapping) {
          config.type = 'dropDown';
          config.api = mapping.api;
          config.displayField = mapping.displayField;
        }
      }

      return config;
    });
  }, [reportDefinition]);

  // Section by options from report definition
  const sectionByOptions = useMemo(() => {
    return reportDefinition?.filterDefinition?.sectionByOptions || [];
  }, [reportDefinition]);

  // All available column options for the field selector
  const fieldOptions = useMemo(() => {
    if (!reportDefinition?.filterDefinition?.columns) return [];
    return reportDefinition.filterDefinition.columns.map((col) => ({
      value: col.field,
      label: col.label,
    }));
  }, [reportDefinition]);

  // Columns filtered to only selected fields, preserving the original column order
  const displayColumns = useMemo(() => {
    if (!reportDefinition?.filterDefinition?.columns) return [];
    const selectedFieldValues = new Set(selectedFields.map((f) => f.value));
    return reportDefinition.filterDefinition.columns.filter((col) =>
      selectedFieldValues.has(col.field)
    );
  }, [reportDefinition, selectedFields]);

  // Initialize useTableFilters
  const {
    tempFilters,
    appliedFilterParams,
    setTempFiltersBatch,
    applyFilters,
    resetFilters,
    renderFilterControl,
    renderFilterBadges,
  } = useTableFilters(tableFilters, {});
  const [hasInitializedDateDefaults, setHasInitializedDateDefaults] = useState(false);

  const lastRunSignatureRef = useRef('');

  const reportOptions = useMemo(() => (
    reports.map((report) => ({
      value: report.id,
      label: report.displayName,
      icon: report.reportIcon || 'mdi:file-chart-outline',
    }))
  ), [reports]);

  const selectedReportOption = useMemo(
    () => reportOptions.find((option) => option.value === selectedReportId) || null,
    [reportOptions, selectedReportId]
  );

  // Fetch accessible reports on mount
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoadingReports(true);
        const data = await getAccessibleReports();
        setReports(data);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports');
      } finally {
        setLoadingReports(false);
      }
    };
    fetchReports();
  }, []);

  // Fetch report definition when a report is selected
  useEffect(() => {
    if (!selectedReportId) {
      setReportDefinition(null);
      setReportData(null);
      setReportMetadata(null);
      setHasInitializedDateDefaults(false);
      return;
    }

    const fetchDefinition = async () => {
      try {
        setLoadingDefinition(true);
        setReportData(null);
        setReportMetadata(null);
        setError(null);
        const data = await getReportDefinition(selectedReportId);
        setReportDefinition(data);
        setHasInitializedDateDefaults(false);
        setSectionBy('none');
        setSortByFields(data?.filterDefinition?.defaultSortByFields || []);
        setPage(1);
        setActivePanelTab('columns');
        setColumnSearch('');

        // Initialize selected fields from defaultDisplayFields
        const defaults = data?.filterDefinition?.defaultDisplayFields;
        const columns = data?.filterDefinition?.columns || [];
        if (defaults && defaults.length > 0) {
          const defaultSet = new Set(defaults);
          setSelectedFields(
            columns
              .filter((col) => defaultSet.has(col.field))
              .map((col) => ({ value: col.field, label: col.label }))
          );
        } else {
          // If no defaults defined, show all columns
          setSelectedFields(columns.map((col) => ({ value: col.field, label: col.label })));
        }
      } catch (err) {
        console.error('Error fetching report definition:', err);
        setError('Failed to load report definition');
      } finally {
        setLoadingDefinition(false);
      }
    };
    fetchDefinition();
  }, [selectedReportId]);

  useEffect(() => {
    if (!reportDefinition?.filterDefinition?.filters?.length) return;

    const filters = reportDefinition.filterDefinition.filters;
    const findDateFilter = (predicate) => filters.find((filter) => {
      if (filter.type !== 'date') return false;
      const label = (filter.label || '').toLowerCase();
      const field = (filter.field || '').toLowerCase();
      return predicate(label, field);
    });

    const fromFilter = findDateFilter(
      (label, field) => (label.includes('from') || field.includes('from')) && (label.includes('date') || field.includes('date'))
    );
    const toFilter = findDateFilter(
      (label, field) => (label.includes('to') || field.includes('to')) && (label.includes('date') || field.includes('date'))
    );

    if (!fromFilter || !toFilter) return;

    const fromValue = tempFilters[fromFilter.field];
    const toValue = tempFilters[toFilter.field];
    const updates = {};
    if (!fromValue) {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30);
      updates[fromFilter.field] = formatDateInputValue(fromDate);
    }
    if (!toValue) {
      updates[toFilter.field] = formatDateInputValue(new Date());
    }

    if (Object.keys(updates).length > 0) {
      setTempFiltersBatch(updates, { apply: true });
    }
  }, [reportDefinition, setTempFiltersBatch, tempFilters]);

  // Validate required filters
  const validateFilters = useCallback(() => {
    if (!reportDefinition?.filterDefinition?.filters) return [];

    const errors = [];
    reportDefinition.filterDefinition.filters.forEach((filter) => {
      if (filter.required) {
        const value = tempFilters[filter.field];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          errors.push(`${filter.label} is required`);
        }
      }
    });
    return errors;
  }, [reportDefinition, tempFilters]);

  const filteredFieldOptions = useMemo(() => {
    if (!columnSearch.trim()) return fieldOptions;
    const query = columnSearch.trim().toLowerCase();
    return fieldOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [columnSearch, fieldOptions]);

  const handleAddField = useCallback((option) => {
    setSelectedFields((prev) => {
      if (prev.find((field) => field.value === option.value)) {
        return prev;
      }
      return [...prev, option];
    });
  }, []);

  const handleRemoveField = useCallback((fieldValue) => {
    setSelectedFields((prev) => prev.filter((field) => field.value !== fieldValue));
  }, []);

  const ReportMenu = (props) => {
    return (
      <selectComponents.Menu {...props}>
        <div className="px-24 pt-16 pb-8 bg-white">
          <div className="fw-semibold text-lg">
            Select a Table
          </div>
        </div>

        {props.children}
      </selectComponents.Menu>
    );
  };

  const ReportMenuList = (props) => (
    <selectComponents.MenuList {...props}>
      <div
        className="d-grid p-16"
        style={{
          gridTemplateColumns: 'repeat(2, minmax(320px, 1fr))',
          gap: '12px',
        }}
      >
        {props.children}
      </div>
    </selectComponents.MenuList>
  );


  const ReportOption = (props) => {
    const { data, isFocused, isSelected } = props;

    return (
      <selectComponents.Option {...props}>
        <div
          className={`border rounded p-3 h-100 d-flex gap-3 ${isFocused || isSelected ? 'bg-light border-primary text-black' : 'bg-white'
            }`}
          style={{ minWidth: 0 }}
        >
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{
              width: 44,
              height: 44,
              backgroundColor: data.bgColor || '#eef2ff',
            }}
          >
            <Icon
              icon={data.icon}
              className="text-primary"
              style={{ fontSize: 20 }}
            />
          </div>

          <div className="d-flex flex-column min-w-0 mt-10">
            <span className="fw-semibold text-md">
              {data.label}
            </span>
          </div>
        </div>
      </selectComponents.Option>
    );
  };


  const ReportSingleValue = (props) => {
    const { data } = props;
    return (
      <selectComponents.SingleValue {...props}>
        <div
          className="d-grid align-items-center"
          style={{ gridTemplateColumns: '36px 1fr', columnGap: '8px' }}
        >
          <Icon icon={data.icon} className="text-primary text-xl" />
          <span>{data.label}</span>
        </div>
      </selectComponents.SingleValue>
    );
  };

  const reportSelectStyles = {
    menu: (base) => ({
      ...base,
      minWidth: 700,
      maxWidth: '90vw',
      overflowX: 'hidden',
      padding: 8,
    }),
    menuList: (base) => ({
      ...base,
      overflowX: 'hidden',
      padding: 0,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      padding: 0,
      cursor: 'pointer',
    }),
  };


  // Run the report
  const handleRunReport = useCallback(async () => {
    if (runInFlightRef.current) {
      return;
    }

    const validationErrors = validateFilters();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      runInFlightRef.current = true;
      setLoadingResults(true);
      setError(null);
      applyFilters();

      // Build params from filters, filtering out empty/null values
      const filterParams = Object.fromEntries(
        Object.entries(appliedFilterParams).filter(([, v]) => {
          if (v === undefined || v === null || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        })
      );

      const params = {
        sectionBy,
        sortBy: sortByFields.length > 0 ? sortByFields : undefined,
        page,
        pageSize,
        timezone: getUserTimeZone(),
        ...filterParams,
      };

      const result = await runReport(selectedReportId, params);
      setReportData(result.data);
      setReportMetadata(result.metadata);
      setTotal(result.total);
    } catch (err) {
      console.error('Error running report:', err);
      setError('Failed to run report');
    } finally {
      runInFlightRef.current = false;
      setLoadingResults(false);
    }
  }, [selectedReportId, appliedFilterParams, sectionBy, sortByFields, page, pageSize, applyFilters, validateFilters]);

  useEffect(() => {
    if (!selectedReportId || !reportDefinition || loadingDefinition) return;
    const signature = JSON.stringify({ selectedReportId, sectionBy, sortByFields, tempFilters });
    if (signature === lastRunSignatureRef.current) return;
    lastRunSignatureRef.current = signature;
    handleRunReport();
  }, [selectedReportId, reportDefinition, loadingDefinition, sectionBy, sortByFields, tempFilters, handleRunReport]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // Re-run report when page changes (after initial run)
  useEffect(() => {
    if (reportData) {
      handleRunReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Export to Excel (all pages)
  const handleExportExcel = useCallback(() => {
    if (!reportData || !reportDefinition) return;
    setShowExportConfirm(true);
  }, [reportData, reportDefinition]);
  
  const doExportExcel = useCallback(async () => {
    setShowExportConfirm(false);
    
    try {
      setExportingExcel(true);

      const filterParams = Object.fromEntries(
        Object.entries(appliedFilterParams).filter(([, v]) => {
          if (v === undefined || v === null || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        })
      );

      const result = await runReport(selectedReportId, {
        sectionBy,
        sortBy: sortByFields.length > 0 ? sortByFields : undefined,
        page: 1,
        pageSize: total,
        timezone: getUserTimeZone(),
        ...filterParams,
      });

      const columns = displayColumns;
      const headers = columns.map((col) => col.label);
      const tz = getUserTimeZone();

      const rows = result.data.map((row) =>
        columns.map((col) => {
          const value = row[col.field];
          if (col.format === 'boolean') {
            return value ? 'Yes' : 'No';
          }
          if (col.format === 'date' && value) {
            return formatLocal(value, tz, 'yyyy-MM-dd HH:mm');
          }
          return value ?? '';
        })
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportDefinition.displayName || 'report'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      setError('Failed to export report');
    } finally {
      setExportingExcel(false);
    }
  }, [displayColumns, total, selectedReportId, appliedFilterParams, sectionBy, sortByFields, reportDefinition]);

  // Loading state
  if (loadingReports) {
    return (
      <div className="card h-100 p-0 rounded-0">
        <div className="card-body d-flex align-items-center justify-content-center">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading reports...
        </div>
      </div>
    );
  }

  // No reports available
  if (reports.length === 0) {
    return (
      <div className="card h-100 p-0 rounded-0">
        <div className="card-body d-flex align-items-center justify-content-center">
          <p className="text-muted">No reports available. Contact your administrator to grant access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header border-0 pb-24">
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* Report Selection */}
          <div className="d-flex flex-column" style={{ minWidth: '310px' }}>
            {/* <label className="form-label">Select a report to get started</label> */}
            <Select
              classNamePrefix="react-select"
              value={selectedReportOption}
              options={reportOptions}
              onChange={(option) => setSelectedReportId(option ? option.value : null)}
              isClearable
              placeholder="-- Select a base table to get started --"
              components={{
                Menu: ReportMenu,
                MenuList: ReportMenuList,
                Option: ReportOption,
                SingleValue: ReportSingleValue,
              }}
              styles={reportSelectStyles}
            />


          </div>

          {/* Filters (when report is selected) */}
          {reportDefinition && !loadingDefinition && (
            <>

              {/* Run Report Button */}
              {/*<div className="d-flex flex-column justify-content-end">
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={handleRunReport}
                  disabled={loadingResults}
                >
                  {loadingResults ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <Icon icon="mdi:play" className="icon" />
                  )}
                  Run Report
                </button>
              </div>*/}

              {/* Reset Button */}
              <div className="d-flex flex-column justify-content-end">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    resetFilters();
                    setSectionBy('none');
                    setSortByFields(reportDefinition?.filterDefinition?.defaultSortByFields || []);
                    setReportData(null);
                    setReportMetadata(null);
                    // Reset display fields to defaults
                    const defaults = reportDefinition?.filterDefinition?.defaultDisplayFields;
                    const columns = reportDefinition?.filterDefinition?.columns || [];
                    if (defaults && defaults.length > 0) {
                      const defaultSet = new Set(defaults);
                      setSelectedFields(
                        columns
                          .filter((col) => defaultSet.has(col.field))
                          .map((col) => ({ value: col.field, label: col.label }))
                      );
                    } else {
                      setSelectedFields(columns.map((col) => ({ value: col.field, label: col.label })));
                    }
                  }}
                >
                  Reset
                </button>
              </div>
            </>
          )}

          {/* Export Button (when data is available) */}
          {reportData && reportData.length > 0 && (
            <div className="ms-auto d-flex flex-column justify-content-end">
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={handleExportExcel}
                disabled={exportingExcel}
              >
                {exportingExcel ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:file-excel" className="icon" />
                    Export Excel
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Filter badges row — show selected values below the filter controls */}
        {reportDefinition && !loadingDefinition && tableFilters.length > 0 && (
          <div className="mt-8" style={{ minHeight: '30px' }}>
            <Icon icon="mdi:filter-outline" className="icon text-xl line-height-1 text-secondary" />
            {renderFilterBadges()}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger mt-3 mb-0">
            {error}
          </div>
        )}
      </div>

      <div className="card-body p-0">
        {loadingDefinition && (
          <div className="d-flex align-items-center justify-content-center py-5">
            <span className="spinner-border spinner-border-sm me-2" />
            Loading report configuration...
          </div>
        )}

        {/*{!selectedReportId && !loadingDefinition && (
          <div className="d-flex align-items-center justify-content-center py-5 text-muted">
            Select a report to get started
          </div>
        )}*/}

        {selectedReportId && reportDefinition && !loadingDefinition && (
          <div className="d-flex align-items-stretch">
            <div className="bg-white mx-16 border radius-8" style={{ width: '400px', minWidth: '400px' }}>
              <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activePanelTab === 'columns' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActivePanelTab('columns')}
                  >
                    Columns
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activePanelTab === 'filters' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActivePanelTab('filters')}
                  >
                    Filters
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activePanelTab === 'grouping' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActivePanelTab('grouping')}
                  >
                    Sort &amp; Group
                  </button>
                </li>
              </ul>
              <div
                className="p-3 d-flex flex-column"
                style={{ height: 'calc(100vh - 236px)' }}
              >
                {activePanelTab === 'columns' && (
                  <>
                    <div className="mb-16">
                      <div className="mb-16">
                        Select the columns to be used in your report.
                      </div>
                      <div className="input-group">
                        <span className="input-group-text bg-white">
                          <Icon icon="mdi:magnify" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search"
                          value={columnSearch}
                          onChange={(e) => setColumnSearch(e.target.value)} />
                      </div>
                    </div>
                    <ul className="list-group flex-grow-1 overflow-auto">
                      {filteredFieldOptions.map((option) => {
                        const isSelected = selectedFields.some((field) => field.value === option.value);
                        return (
                          <li
                            key={option.value}
                            className={`list-group-item d-flex align-items-center ${isSelected ? 'text-muted' : ''} ${isSelected ? 'bg-primary-focus' : ''}`}
                          >
                            <span>{option.label}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link ms-auto"
                              onClick={() => handleAddField(option)}
                              disabled={isSelected}
                            >
                              <Icon
                                icon={isSelected ? "mdi:check" : "mdi:plus"}
                                className="text-lg text-primary"
                              />
                            </button>
                          </li>
                        );
                      })}
                      {filteredFieldOptions.length === 0 && (
                        <li className="list-group-item text-muted">No fields match your search.</li>
                      )}
                    </ul>
                  </>
                )}
                {activePanelTab === 'filters' && (
                  <div className="d-flex flex-column gap-3">
                    <div>
                      Use filters to select the information you need.
                    </div>
                    <button type="button" className="btn btn-outline-primary w-100">
                      <Icon icon="mdi:plus" className="me-2 text-lg" style={{ position: "relative", top: "-2px" }} />
                      Add Filter
                    </button>
                    <div className="d-flex flex-column gap-2">

                      {reportDefinition && !loadingDefinition && (
                        <>
                          {tableFilters.map((config) => renderFilterControl(config, false, 'col-md-12'))}
                        </>
                      )}
                      {(!reportDefinition.filterDefinition?.filters || reportDefinition.filterDefinition.filters.length === 0) && (
                        <div className="text-muted">No filters available.</div>
                      )}
                    </div>
                  </div>
                )}
                {activePanelTab === 'grouping' && (
                  <div className="d-flex flex-column gap-3">
                    <div>
                      Select sorting and grouping for the report.
                    </div>

                    {reportDefinition && !loadingDefinition && (
                      <div className="d-flex flex-column gap-3">
                        {/* Sort By section */}
                        <div className="d-flex flex-column gap-2">
                          <label className="form-label mb-0 text-lg">Sort By</label>
                          {/* Add sort field dropdown */}
                          {fieldOptions.length > 0 && (
                            <div className="mb-16">
                              <Select
                                classNamePrefix="react-select"
                                options={fieldOptions.filter(
                                  (opt) => !sortByFields.some((s) => s.fieldName === opt.value)
                                )}
                                value={null}
                                onChange={(option) => {
                                  if (option) {
                                    setSortByFields((prev) => [
                                      ...prev,
                                      { fieldName: option.value, ascending: true },
                                    ]);
                                  }
                                }}
                                placeholder="Add sort field..."
                                isClearable={false}
                              />
                            </div>
                          )}

                          {/* Current sort fields */}

                          {sortByFields.map((sortField, index) => {
                            const colLabel = fieldOptions.find((f) => f.value === sortField.fieldName)?.label || sortField.fieldName;
                            return (
                              <div key={index} className="d-flex align-items-center gap-2">
                                <div className="d-flex flex-column flex-grow-1 gap-1">
                                  <span className="fw-semibold text-sm text-primary">{colLabel}</span>
                                  <select
                                    className="form-select form-select-sm"
                                    value={sortField.ascending ? 'asc' : 'desc'}
                                    onChange={(e) => {
                                      setSortByFields((prev) => prev.map((f, i) =>
                                        i === index ? { ...f, ascending: e.target.value === 'asc' } : f
                                      ));
                                    }}
                                  >
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                  </select>
                                </div>
                                <div className="d-flex flex-column gap-1">
                                  {index > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-muted"
                                      title="Move up"
                                      onClick={() => {
                                        setSortByFields((prev) => {
                                          const next = [...prev];
                                          [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                          return next;
                                        });
                                      }}
                                    >
                                      <Icon icon="mdi:chevron-up" className="text-primary text-xl" style={{ position: "relative", top: "12px" }} />
                                    </button>
                                  )}
                                  {index < sortByFields.length - 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-muted"
                                      title="Move down"
                                      onClick={() => {
                                        setSortByFields((prev) => {
                                          const next = [...prev];
                                          [next[index], next[index + 1]] = [next[index + 1], next[index]];
                                          return next;
                                        });
                                      }}
                                    >
                                      <Icon icon="mdi:chevron-down" className="text-primary text-xl" style={{ position: "relative", top: "12px" }} />
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0 text-muted"
                                  title="Remove"
                                  onClick={() => {
                                    setSortByFields((prev) => prev.filter((_, i) => i !== index));
                                  }}
                                >
                                  <Icon icon="mdi:close" className="text-muted text-lg" style={{ position: "relative", top: "10px" }} />
                                </button>
                              </div>
                            );

                          })}

                          {sortByFields.length === 0 && (
                            <div className="text-muted text-sm">
                              No sort fields selected. The report will use its default ordering.
                            </div>
                          )}
                        </div>

                        {/* Section By (Group By) dropdown */}
                        {sectionByOptions.length > 0 && (
                          <div className="d-flex flex-column mt-24" style={{ minWidth: '180px' }}>
                            <label className="form-label text-lg">Group By</label>
                            <select
                              className="form-select"
                              value={sectionBy}
                              onChange={(e) => setSectionBy(e.target.value)}
                            >
                              {sectionByOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-grow-1 px-3" style={{ minWidth: "0" }}>
              {selectedReportId && reportDefinition && !loadingDefinition && !reportData && !loadingResults && (
                <div className="d-flex align-items-center justify-content-center py-5 text-muted">
                  Set your filters and click "Run Report" to view results
                </div>
              )}

              {loadingResults && (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Running report...
                </div>
              )}

              {/* Report Results */}
              {reportData && reportDefinition && !loadingResults && (
                <ReportDataTable
                  data={reportData}
                  columns={displayColumns}
                  metadata={reportMetadata}
                  total={total}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onRemoveColumn={handleRemoveField}
                />
              )}
            </div>
          </div>
        )}
      </div>
 
      {showExportConfirm && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Export Report</h5>
                  <button className="btn-close" onClick={() => setShowExportConfirm(false)} />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Exporting the full report may take some time and produce a large file
                    ({total.toLocaleString()} rows). Do you want to continue?
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowExportConfirm(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={doExportExcel}>
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default ReportsLayer;